import usePermissions from "@/hooks/use-permissions.hook.ts";
import { useWorkflowBlockEdit } from "@/pages/workflows/edit/flow/use-workflow-block-edit.tsx";
import { useWorkflowCreateBlock } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowBlockDelayReference,
  ApiWorkflowBlockTypes,
  ApiWorkflowTriggerScheduleDayType,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { isEmployeeOnlyBlockAction } from "@/types/workflows/workflow.subject.types.ts";
import { ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  FontAwesomeIcon,
} from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

interface WorkflowAddBlockMenuProps {
  workflowId: number;
  workflowVersion: ApiWorkflowVersion;
  fromBlockId: number;
}

export const WorkflowAddBlockMenuItems = ({
  workflowId,
  workflowVersion,
  fromBlockId,
}: WorkflowAddBlockMenuProps) => {
  const { t } = useTranslation();
  const workflowBlockCreate = useWorkflowCreateBlock();
  const { handleWorkflowBlockEdit } = useWorkflowBlockEdit();
  const { isFeatureEnabled } = usePermissions();

  const isRecruitingSubject = isCandidateWorkflowEventType(
    workflowVersion.trigger_event?.event_type
  );
  const canAddBlockForSubject = (actionType: ApiWorkflowBlockActionTypes) =>
    !isRecruitingSubject || !isEmployeeOnlyBlockAction(actionType);

  const handleAddBlock = async (actionType: ApiWorkflowBlockActionTypes) => {
    try {
      const createdBlock = await workflowBlockCreate.mutateAsync({
        workflowId,
        workflowVersionId: workflowVersion.id,
        payload: {
          block_type: ApiWorkflowBlockTypes.action,
          action_type: actionType,
          delay_days: 0,
          delay_hours: 0,
          delay_minutes: 0,
          delay_reference: workflowVersion.trigger_schedule_rule
            ? ApiWorkflowBlockDelayReference.trigger_date
            : ApiWorkflowBlockDelayReference.previous_block,
          delay_day_type: ApiWorkflowTriggerScheduleDayType.calendar,
          title: "",
          from_block_id: fromBlockId,
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockCreated, {
        [analyticProperties.id]: workflowId,
        [analyticProperties.actionType]: actionType,
      });

      handleWorkflowBlockEdit(createdBlock, workflowVersion);
    } catch {
      // log
    }
  };

  const options = (
    <>
      {isFeatureEnabled(ApiFeatureType.tasks) && (
        <DropdownMenuItem
          title={t("tasks:task")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-check"} />}
          onClick={() => {
            void handleAddBlock(ApiWorkflowBlockActionTypes.task);
          }}
        />
      )}
      {isFeatureEnabled(ApiFeatureType.core_company_calendar) && (
        <DropdownMenuItem
          title={t("workflow:action_type_meeting")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-calendar"} />}
          onClick={() => {
            void handleAddBlock(ApiWorkflowBlockActionTypes.meeting);
          }}
        />
      )}
      <DropdownMenuItem
        title={t("workflow:action_type_notification")}
        leftIcon={<FontAwesomeIcon icon={"fa-light fa-envelope"} />}
        onClick={() => {
          void handleAddBlock(ApiWorkflowBlockActionTypes.send_notification);
        }}
      />
      {isCandidateWorkflowEventType(workflowVersion.trigger_event?.event_type) && (
        <DropdownMenuSub
          title={t("workflow:action_group_candidate")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-user"} />}
        >
          <DropdownMenuItem
            title={t("workflow:action_type_candidate_email")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-envelope"} />}
            onClick={() => {
              void handleAddBlock(ApiWorkflowBlockActionTypes.candidate_email);
            }}
          />
          <DropdownMenuItem
            title={t("workflow:action_type_candidate_stage_change")}
            leftIcon={<FontAwesomeIcon icon={"fa-light fa-arrow-right-arrow-left"} />}
            onClick={() => {
              void handleAddBlock(ApiWorkflowBlockActionTypes.candidate_stage_change);
            }}
          />
        </DropdownMenuSub>
      )}
      {isFeatureEnabled(ApiFeatureType.esign) &&
        canAddBlockForSubject(ApiWorkflowBlockActionTypes.esign) && (
          <DropdownMenuItem
            title={t("workflow:action_type_esign")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-signature" />}
            onClick={() => {
              void handleAddBlock(ApiWorkflowBlockActionTypes.esign);
            }}
          />
        )}
      {canAddBlockForSubject(ApiWorkflowBlockActionTypes.invitation) && (
        <DropdownMenuItem
          title={t("core:invite_to_skailer")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-envelope-circle-user" />}
          onClick={() => {
            void handleAddBlock(ApiWorkflowBlockActionTypes.invitation);
          }}
        />
      )}
      <DropdownMenuSeparator />
      {isFeatureEnabled(ApiFeatureType.talent_surveys) && (
        <DropdownMenuSub
          title={t("workflow:action_group_survey")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-square-poll-horizontal" />}
        >
          <DropdownMenuItem
            title={t("workflow:action_type_survey")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-square-poll-horizontal" />}
            onClick={() => {
              void handleAddBlock(ApiWorkflowBlockActionTypes.survey);
            }}
          />
          {canAddBlockForSubject(ApiWorkflowBlockActionTypes.survey_enroll) && (
            <DropdownMenuItem
              title={t("workflow:action_type_survey_enroll")}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-user-circle-plus" />}
              onClick={() => {
                void handleAddBlock(ApiWorkflowBlockActionTypes.survey_enroll);
              }}
            />
          )}
          {isFeatureEnabled(ApiFeatureType.approvals) &&
            canAddBlockForSubject(ApiWorkflowBlockActionTypes.survey_with_approval) && (
              <DropdownMenuItem
                title={t("workflow:action_type_approval")}
                leftIcon={<FontAwesomeIcon icon="fa-light fa-check-double" />}
                onClick={() => {
                  void handleAddBlock(ApiWorkflowBlockActionTypes.survey_with_approval);
                }}
              />
            )}
        </DropdownMenuSub>
      )}
      {isFeatureEnabled(ApiFeatureType.talent_performance_reviews) && (
        <DropdownMenuSub
          title={t("workflow:action_group_review")}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-square-list" />}
        >
          <DropdownMenuItem
            title={t("performance_review:start_review")}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-square-list" />}
            onClick={() => {
              void handleAddBlock(ApiWorkflowBlockActionTypes.performance_review);
            }}
          />
          {canAddBlockForSubject(ApiWorkflowBlockActionTypes.performance_review_enroll) && (
            <DropdownMenuItem
              title={t("workflow:action_type_performance_review_add_employee")}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-circle-user-circle-plus" />}
              onClick={() => {
                void handleAddBlock(ApiWorkflowBlockActionTypes.performance_review_enroll);
              }}
            />
          )}
        </DropdownMenuSub>
      )}

      <DropdownMenuItem
        title={t("workflow:action_type_run_workflow")}
        leftIcon={<FontAwesomeIcon icon={"fa-light fa-diagram-project"} />}
        onClick={() => {
          void handleAddBlock(ApiWorkflowBlockActionTypes.run_workflow);
        }}
      />
      <DropdownMenuSeparator />
      {canAddBlockForSubject(ApiWorkflowBlockActionTypes.google_workspace) && (
        <DropdownMenuItem
          title={t("workflow:action_type_google_workspace")}
          description={t("workflow:action_type_google_workspace_description")}
          leftIcon={<FontAwesomeIcon icon={"fa-brands fa-google"} />}
          onClick={() => {
            void handleAddBlock(ApiWorkflowBlockActionTypes.google_workspace);
          }}
        />
      )}
      <DropdownMenuItem
        title={t("workflow:action_type_http_request")}
        description={t("workflow:action_type_http_request_description")}
        leftIcon={<FontAwesomeIcon icon="fa-light fa-gear-api" />}
        onClick={() => {
          void handleAddBlock(ApiWorkflowBlockActionTypes.http_request);
        }}
      />
    </>
  );

  return options;
};

export const WorkflowAddBlockMenuSub = ({
  workflowId,
  workflowVersion,
  fromBlockId,
}: WorkflowAddBlockMenuProps) => {
  const { t } = useTranslation();

  return (
    <DropdownMenuSub
      title={
        <span className={"inline-flex items-center gap-2"}>
          <FontAwesomeIcon icon={"fa-light fa-plus"} />
          {t("core:add_action")}
        </span>
      }
    >
      <WorkflowAddBlockMenuItems
        workflowId={workflowId}
        workflowVersion={workflowVersion}
        fromBlockId={fromBlockId}
      />
    </DropdownMenuSub>
  );
};
